import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Scissors,
  Plus,
  RefreshCcw,
  Filter,
} from "lucide-react";

import { ApiError, formatCLP, formatDate, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Button,
  Card,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
} from "../../components/ui";
import {
  abrirOrden,
  listarActivas,
  marcarVencidas,
  type AbrirOrdenPayload,
  type EstadoOrdenCorte,
  type OrdenCorteRead,
} from "./api";
import { EstadoCorteBadge } from "./estado";
import { fetchContratos, type ContratoOperativo } from "../socios/api";
import { deudaDelContrato } from "../contratos/api";

const ESTADOS_FILTRO: { value: EstadoOrdenCorte | "TODOS"; label: string }[] = [
  { value: "TODOS", label: "Activas: abiertas, notificadas, en ruta y cortadas" },
  { value: "Pendiente", label: "Abiertas" },
  { value: "Notificada", label: "Notificadas" },
  { value: "EnRuta", label: "En ruta" },
  { value: "Cortada", label: "Cortadas" },
];

export function CortesListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const estadoUrl = params.get("estado");
  // `?nueva=1&contrato=<id>`: lo manda la cuenta corriente para abrir la
  // orden de esa casa sin buscarla de nuevo.
  const contratoUrl = Number(params.get("contrato"));
  const [contratoInicial] = useState<number | null>(
    params.get("nueva") === "1" && Number.isInteger(contratoUrl) && contratoUrl > 0
      ? contratoUrl
      : null,
  );
  const [filtroEstado, setFiltroEstado] = useState<EstadoOrdenCorte | "TODOS">(
    ESTADOS_FILTRO.some((e) => e.value === estadoUrl)
      ? (estadoUrl as EstadoOrdenCorte | "TODOS")
      : "TODOS",
  );
  const [openForm, setOpenForm] = useState(params.get("nueva") === "1");

  function cerrarForm(): void {
    setOpenForm(false);
    // Sin limpiar, recargar volvería a abrir el formulario.
    if (params.has("nueva") || params.has("contrato")) {
      const next = new URLSearchParams(params);
      next.delete("nueva");
      next.delete("contrato");
      setParams(next, { replace: true });
    }
  }

  const ordenesQ = useQuery({
    queryKey: ["ordenes-corte", filtroEstado],
    queryFn: () =>
      listarActivas(filtroEstado === "TODOS" ? undefined : filtroEstado),
  });

  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
  });

  const contratoById = useMemo(() => {
    const m = new Map<number, ContratoOperativo>();
    for (const c of contratosQ.data ?? []) m.set(c.id, c);
    return m;
  }, [contratosQ.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Cortes"
        icon={Scissors}
        subtitle={
          ordenesQ.data
            ? `${ordenesQ.data.length} ${ordenesQ.data.length === 1 ? "orden activa" : "órdenes activas"}`
            : undefined
        }
        actions={
          <>
            <RecalcularVencidasButton />
            <Button icon={Plus} onClick={() => setOpenForm((v) => !v)}>
              Abrir orden de corte
            </Button>
          </>
        }
      />

      {openForm && (
        <AbrirOrdenForm
          contratos={contratosQ.data ?? []}
          contratoInicialId={contratoInicial}
          onClose={cerrarForm}
        />
      )}

      <Card padding="sm">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={filtroEstado}
            onChange={(e) =>
              setFiltroEstado(e.target.value as EstadoOrdenCorte | "TODOS")
            }
            aria-label="Filtrar por estado"
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {ESTADOS_FILTRO.map((e) => (
              <option key={e.value} value={e.value}>
                {e.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-neutral-600">
            No muestra repuestas ni anuladas. El historial de cada socio está
            en su cuenta corriente.
          </span>
        </div>
      </Card>

      {ordenesQ.isPending && <Cargando />}
      {ordenesQ.isError && (
        <ErrorCarga
          error={ordenesQ.error}
          mensaje="Error al cargar órdenes."
          onReintentar={() => void ordenesQ.refetch()}
        />
      )}

      {ordenesQ.data && ordenesQ.data.length === 0 && (
        <p className="text-sm text-emerald-700">
          Sin órdenes de corte activas. ✓
        </p>
      )}

      {ordenesQ.data && ordenesQ.data.length > 0 && (
        <TableWrap label="Órdenes de corte">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Arranque</th>
                <th scope="col" className="px-3 py-2 text-left">Generada</th>
                <th scope="col" className="px-3 py-2 text-right">Deuda</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-left">Programado</th>
                <th scope="col" className="px-3 py-2 text-right">{""}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {ordenesQ.data.map((o) => (
                <OrdenRow
                  key={o.id}
                  orden={o}
                  contrato={contratoById.get(o.contrato_id)}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function OrdenRow({
  orden,
  contrato,
}: {
  orden: OrdenCorteRead;
  contrato: ContratoOperativo | undefined;
}): JSX.Element {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2">
        <div className="font-medium text-neutral-900">
          {contrato?.socio_nombre ?? `contrato #${orden.contrato_id}`}
        </div>
        <div className="text-xs text-neutral-500">{contrato?.socio_rut ?? ""}</div>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        <div>{contrato?.arranque_codigo ?? "—"}</div>
        <div className="text-xs text-neutral-500">
          {contrato?.arranque_direccion ?? ""}
        </div>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        {formatDateTime(orden.fecha_generacion)}
      </td>
      <td className="px-3 py-2 text-right font-medium">
        {formatCLP(orden.monto_deuda_al_corte)}
      </td>
      <td className="px-3 py-2">
        <EstadoCorteBadge estado={orden.estado} />
      </td>
      <td className="px-3 py-2 text-neutral-600">
        {orden.fecha_corte_programada
          ? formatDate(orden.fecha_corte_programada)
          : "—"}
      </td>
      <td className="px-3 py-2 text-right">
        <Link
          to={`/cortes/${orden.id}`}
          state={{ desde: { etiqueta: "Cortes", ruta: `/cortes${window.location.search}` } }}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          Detalle
          <ArrowRight className="h-3 w-3" />
        </Link>
      </td>
    </tr>
  );
}

interface FormProps {
  contratos: ContratoOperativo[];
  /** Contrato preseleccionado (llega por `?nueva=1&contrato=`). */
  contratoInicialId?: number | null;
  onClose: () => void;
}

function AbrirOrdenForm({
  contratos,
  contratoInicialId = null,
  onClose,
}: FormProps): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [contratoId, setContratoId] = useState<string>(
    contratoInicialId != null ? String(contratoInicialId) : "",
  );
  const [buscar, setBuscar] = useState("");
  // Con un contrato preseleccionado (viene de la ficha del socio) no se
  // muestra el buscador: el contrato queda fijo hasta que se pide "Cambiar".
  const [eligiendo, setEligiendo] = useState(contratoInicialId == null);
  const contratoFijo = contratos.find((c) => String(c.id) === contratoId);
  // La deuda sale del sistema (boletas impagas de esa casa), no se tipea:
  // escrita a mano quedaba en $0 si el campo iba vacío.
  const deudaQ = useQuery({
    queryKey: ["contrato", Number(contratoId), "deuda"],
    queryFn: () => deudaDelContrato(Number(contratoId)),
    enabled: contratoId !== "",
  });
  const deuda = deudaQ.data?.deuda;
  const contratosFiltrados = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return contratos;
    return contratos.filter((c) =>
      [c.socio_nombre, c.socio_rut, c.arranque_codigo, c.arranque_direccion]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [contratos, buscar]);
  const [fechaProgramada, setFechaProgramada] = useState<string>("");
  const [observaciones, setObservaciones] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (p: AbrirOrdenPayload) => abrirOrden(p),
    onSuccess: (orden) => {
      toast.success(`Orden de corte #${orden.id} abierta`);
      void qc.invalidateQueries({ queryKey: ["ordenes-corte"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo abrir";
      setError(msg);
      toast.error(msg);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Abrir orden de corte"
      icon={Scissors}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (!contratoId) {
                setError("Elige un contrato");
                return;
              }
              if (deuda === undefined) {
                setError("Espera a que cargue la deuda del contrato");
                return;
              }
              if (deuda <= 0) {
                setError("Este contrato no tiene deuda impaga: no corresponde corte");
                return;
              }
              mutation.mutate({
                contrato_id: Number(contratoId),
                monto_deuda_al_corte: deuda,
                fecha_corte_programada: fechaProgramada || null,
                observaciones: observaciones.trim() || null,
              });
            }}
            disabled={mutation.isPending || deudaQ.isFetching}
          >
            {mutation.isPending ? "Abriendo…" : "Abrir orden"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
      {eligiendo ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Buscar contrato"
            type="search"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Nombre, RUT, código o dirección"
            disabled={mutation.isPending}
          />
          <Select
            label={`Contrato (${contratosFiltrados.length})`}
            value={contratoId}
            onChange={(e) => setContratoId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">— elegir —</option>
            {contratosFiltrados.map((c) => (
              <option key={c.id} value={c.id}>
                {c.socio_nombre} ({c.arranque_codigo ?? "s/cód"})
              </option>
            ))}
          </Select>
        </div>
      ) : (
        <div className="text-sm">
          <div className="font-medium text-neutral-700">Contrato</div>
          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-neutral-900">
              {contratoFijo
                ? `${contratoFijo.socio_nombre} · ${contratoFijo.arranque_codigo ?? "s/cód"}`
                : contratos.length === 0
                  ? "Cargando contrato…"
                  : `Contrato #${contratoId}`}
            </span>
            <Button
              variant="link"
              size="sm"
              onClick={() => setEligiendo(true)}
              disabled={mutation.isPending}
            >
              Cambiar
            </Button>
          </div>
          {contratoFijo?.arranque_direccion && (
            <p className="mt-0.5 text-xs text-neutral-500">
              {contratoFijo.arranque_direccion}
            </p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="text-sm">
          <div className="font-medium text-neutral-700">Deuda al corte</div>
          <div className="mt-1.5 tabular-nums text-neutral-900">
            {contratoId === ""
              ? "Elige un contrato"
              : deudaQ.isPending
                ? "Calculando…"
                : deudaQ.isError
                  ? "No se pudo calcular la deuda"
                  : formatCLP(deuda ?? 0)}
          </div>
          <p className="mt-0.5 text-xs text-neutral-500">
            Boletas impagas de esa casa, según el sistema.
          </p>
        </div>
        <Input
          label="Fecha programada de corte (opcional)"
          type="date"
          value={fechaProgramada}
          onChange={(e) => setFechaProgramada(e.target.value)}
          disabled={mutation.isPending}
        />
      </div>
      <Input
        label="Observaciones"
        type="text"
        maxLength={2000}
        value={observaciones}
        onChange={(e) => setObservaciones(e.target.value)}
        disabled={mutation.isPending}
        placeholder="ej. 3 boletas vencidas, sin convenio"
      />

      {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
    </Modal>
  );
}

function RecalcularVencidasButton(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: marcarVencidas,
    onSuccess: (res) => {
      if (res.afectadas === 0) {
        toast.info("Sin nuevas vencidas");
      } else {
        toast.success(
          `${res.afectadas} boleta(s) marcadas como Vencida`,
        );
      }
      void qc.invalidateQueries({ queryKey: ["lecturas"] });
      void qc.invalidateQueries({ queryKey: ["lecturas-pendientes"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "Error al recalcular");
    },
  });

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-200 disabled:opacity-50"
      title="Marca como Vencidas las boletas emitidas cuya fecha de vencimiento ya pasó"
    >
      <RefreshCcw
        className={`h-4 w-4 ${mutation.isPending ? "animate-spin" : ""}`}
      />
      Recalcular vencidas
    </button>
  );
}
